import * as React from 'react';
import { IHeader } from "../../common/ItemCreation";
import * as Metadata from "../../../entities/Metadata";
import { getLabel } from "../../../entities/Metadata";
import { LayoutsState, actionCreators } from "../../../store/layouts";
import { connect } from 'react-redux';
import { ApplicationState } from '../../../store';
import { EntityType, entityTypeLabelMap } from '../../../entities/common';
import { bindActionCreators } from 'redux';
import { LayoutService } from '../../utils/LayoutService';
import LabellableComponent from '../LabellableComponent';
import * as analytics from '../../../analytics';
import { UserState } from '../../../store/User';
import ListEntityWithNameCreation from '../ListEntityWithNameCreation';
import EntityLayoutsDropdown from './EntityLayoutsDropdown';
import Collapsible from '../Collapsible';

type AdvancedOptions = {
    isCollapsible?: boolean;
    hideTitle?: boolean;
    onRender?: (updateIsDirty: (isDirty: boolean) => void) => React.ReactNode;
    disabled?: boolean;
}

type OwnProps = {
    entityType: EntityType;
    entityTypeLabel?: string;
    onDismiss: () => void;
    onSave: (name: string, layoutId: string) => void;
    hideSectionSelector?: boolean;
    header?: Partial<IHeader>;
    defaultLayoutId?: string;
    advancedOptions?: AdvancedOptions;
};

type StateProps = {
    user: UserState;
    layouts: LayoutsState;
    nameField?: Metadata.Field;
};

type IProps = ReturnType<typeof actionCreators.forEntity> & StateProps & OwnProps;

const EntityCreation = (props: React.PropsWithChildren<IProps>) => {
    const { layouts, defaultLayoutId, user, entityType, hideSectionSelector, advancedOptions, header, onDismiss, nameField } = props;
    const [selectedLayoutId, setSelectedLayoutId] = React.useState(LayoutService.getDefault(layouts).id);

    React.useEffect(() => {
        const layoutId = defaultLayoutId && layouts.allIds.includes(defaultLayoutId)
            ? defaultLayoutId
            : LayoutService.getDefault(layouts).id;
        setSelectedLayoutId(layoutId);
    }, [defaultLayoutId, layouts])

    const entityTypeLabel = props.entityTypeLabel ?? entityTypeLabelMap[props.entityType].singular;

    const onChangeLayout = React.useCallback((layoutId: string | undefined, updateIsDirty: (isDirty: boolean) => void) => {
        if (!layoutId) {
            return;
        }
        updateIsDirty(true);
        setSelectedLayoutId(layoutId);
    }, [setSelectedLayoutId]);

    const onSave = React.useCallback((name: string) => {
        if (name) {
            props.onSave(name, selectedLayoutId);
            analytics.trackCreate(user, { itemType: entityType, itemTitle: name });
        }
    }, [props.onSave, onDismiss, user, selectedLayoutId]);

    const buildLayoutFields = React.useCallback((updateIsDirty: (isDirty: boolean) => void) => {
        return !hideSectionSelector ? <>
            <div className="grid-item">
                {!advancedOptions?.hideTitle && <span>Select a layout that will be applied to this {entityTypeLabel}</span>}
                <LabellableComponent label='Layout' className="field-container">
                    <EntityLayoutsDropdown
                        entityType={entityType}
                        layoutId={selectedLayoutId}
                        onChanged={(_) => onChangeLayout(_?.id, updateIsDirty)}
                    />
                </LabellableComponent>
            </div>
            <div className="grid-item">
                <LabellableComponent label='Layout Sections' className="field-container">
                    <ul className='simple-list'>
                        {
                            layouts.byId[selectedLayoutId].sections
                                .filter(_ => _.isSelected)
                                .map(_ => <li className='item' key={_.id}>{getLabel(_)}</li>)
                        }
                    </ul>
                </LabellableComponent>
            </div>
        </> : undefined
    }, [selectedLayoutId, onChangeLayout, hideSectionSelector, entityTypeLabel, entityType, layouts]);

    return (
        <ListEntityWithNameCreation
            entityType={entityType}
            header={{ nameEditorDescription: nameField?.description, ...header }}
            disabled={advancedOptions?.disabled}
            onSave={onSave}
            onDismiss={onDismiss}
        >
            {(updateIsDirty) => (
                <div className="panel-area">
                    {
                        !!advancedOptions?.isCollapsible || advancedOptions?.onRender
                            ? <div className="grid-item">
                                <Collapsible key='advanced-options' title='Advanced options' iconName='PPMXGear' shouldDismount={false}>
                                    {advancedOptions.onRender?.(updateIsDirty)}
                                    {buildLayoutFields(updateIsDirty)}
                                </Collapsible>
                            </div>
                            : <>
                                {props.children}
                                {buildLayoutFields(updateIsDirty)}
                            </>

                    }
                </div>
            )}
        </ListEntityWithNameCreation>
    );
}

function mapStateToProps(state: ApplicationState, ownProps: OwnProps): StateProps {
    const fields = state.fields[ownProps.entityType];
    return {
        user: state.user,
        layouts: state.layouts[ownProps.entityType],
        nameField: fields && Metadata.getNameField(fields.allIds.map(_ => fields.byId[_]))
    };
}

export default connect(mapStateToProps, (dispatch, ownProps) => bindActionCreators(actionCreators.forEntity(ownProps.entityType), dispatch))(EntityCreation);